import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { injectIntl, intlShape } from 'react-intl';
import injectSheet from 'react-jss';
import { Field } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import creditCardType from 'credit-card-type';
import TextInput from '../../../TextInput';
import { required } from '../../../lib/validate';
import cardsSVG from './cardImages';
import FormLabel from '../../../FormLabel';
import { noSpaces } from '../../../lib/normalize';

const cardNumberMasks = {
  'american-express': '9999 999999 99999',
  'diners-club': '9999 999999 999999999',
  elo: '9999 9999 9999 9999',
  mastercard: '9999 9999 9999 9999',
};

const cardSecurityInfo = cardType =>
  cardType === 'american-express'
    ? {
        mask: '9999',
        hint: 'ccvAmexHint',
      }
    : { mask: '999', hint: 'ccvHint' };

class CreditCardFields extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    classes: PropTypes.object.isRequired,
    setInstallments: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    installments: PropTypes.array,
    cardNumber: PropTypes.string,
    ccv: PropTypes.string,
  };

  static defaultProps = {
    installments: undefined,
    cardNumber: undefined,
    ccv: undefined,
  };

  getCard(cardNumber) {
    const matchedCards = creditCardType(cardNumber);
    if (matchedCards.length === 1) {
      return matchedCards[0];
    }
  }

  updateCcv = ({ target: { value } }) => {
    const { onChange, ccv } = this.props;
    const card = this.getCard(value);
    if (card && ccv) {
      onChange('creditCard.ccv', ccv.substring(0, card.code.size));
    }
  };

  render() {
    const { intl, classes, installments, setInstallments, cardNumber } = this.props;
    const card = this.getCard(cardNumber);
    const cardType = (card || {}).type;
    const { mask: ccvMask, hint: ccvHint } = cardSecurityInfo(cardType);

    return (
      <Fragment>
        <div className={classNames(classes.section, classes.installment)}>
          {installments && installments.length > 1 && (
            <Field
              component={TextInput}
              name="installmentsCount"
              props={{ name: 'installments', variant: 'select' }}
              label={<FormattedMessage id="installments" />}
              className={classes.input}
              validate={required}
              onChange={event => setInstallments(installments[event.target.value - 1])}
              required
            >
              {installments.map(installment => (
                <option value={installment.installmentCount} key={installment.installmentCount}>
                  {intl.formatMessage(
                    {
                      id: 'installmentOptionLabel',
                    },
                    {
                      installmentCount: installment.installmentCount,
                      installmentValue: intl.formatNumber(installment.installmentValue, {
                        style: 'currency',
                        currency: 'BRL',
                      }),
                      interest: intl.formatNumber(installment.interest, {
                        style: 'percent',
                        maximumFractionDigits: 2,
                      }),
                    },
                  )}
                </option>
              ))}
            </Field>
          )}
          <div className={classes.creditCardsAccepted}>
            <FormLabel>
              <FormattedMessage id="creditCardsAccepted" />
            </FormLabel>
            {Object.keys(cardsSVG).map(card => (
              <img
                src={cardsSVG[card]}
                alt={<FormattedMessage id={card} />}
                key={card}
                className={classNames(
                  classes.cardImage,
                  cardType && cardType !== card ? classes.cardImageDisabled : undefined,
                )}
              />
            ))}
          </div>
        </div>
        <div className={classNames(classes.section, classes.row)}>
          <Field
            component={TextInput}
            type="tel"
            name="creditCard.number"
            props={{ name: 'cardnumber' }}
            label={<FormattedMessage id="ccNumber" />}
            className={classes.input}
            validate={required}
            normalize={noSpaces}
            mask={cardNumberMasks[cardType] || '9999 9999 9999 9999999'}
            onChange={this.updateCcv}
            maskChar=""
            required
          />
        </div>
        <div className={classes.row}>
          <Field
            component={TextInput}
            type="tel"
            props={{ name: 'cvc' }}
            name="creditCard.ccv"
            label={<FormattedMessage id="ccv" />}
            className={classes.input}
            validate={required}
            mask={ccvMask}
            maskChar=""
            hint={ccvHint}
            required
          />
          <Field
            component={TextInput}
            type="tel"
            props={{ name: 'exp-date' }}
            name="creditCard.expiration"
            label={<FormattedMessage id="expiration" />}
            placeholder={intl.formatMessage({ id: 'expirationPlaceholder' })}
            className={classNames(classes.input, classes.smallInput)}
            validate={required}
            mask="99/99"
            required
          />
        </div>
        <div className={classNames(classes.section, classes.row)}>
          <Field
            component={TextInput}
            name="creditCard.holder"
            props={{ name: 'ccname' }}
            label={<FormattedMessage id="holderName" />}
            className={classes.input}
            validate={required}
            required
          />
        </div>
      </Fragment>
    );
  }
}

const stylesheet = theme => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    '&:not(:first-child)': {
      marginTop: theme.spacing.unit,
    },
    '&:last-child': {
      marginBottom: theme.spacing.medium,
    },
    [theme.breakpoints.up('sm')]: {
      flexWrap: 'nowrap',
    },
  },
  section: {
    '&:not(:first-child)': {
      marginTop: theme.spacing.medium,
    },
  },
  input: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      '&:not(:first-child)': {
        marginTop: theme.spacing.unit,
      },
    },
    [theme.breakpoints.up('sm')]: {
      '&:not(:first-child)': {
        marginLeft: theme.spacing.unit,
      },
    },
  },
  cardImage: {
    '&:not(:first-child)': {
      marginLeft: theme.spacing.unit,
    },
    transition: 'opacity 1s ease-in-out',
  },
  cardImageDisabled: {
    opacity: theme.opacity.disabled,
  },
  smallInput: {
    [theme.breakpoints.up('sm')]: {
      width: '75%',
    },
  },
  installment: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  creditCardsAccepted: {
    width: '100%',
    height: 57, // Same size as the installment input that will render later
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
    },
    '&:not(:first-child)': {
      [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing.medium,
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing.medium,
      },
    },
  },
});

export default compose(
  injectIntl,
  injectSheet(stylesheet),
)(CreditCardFields);



// WEBPACK FOOTER //
// ./src/site-render/src/RegisterExtension/OrderPaymentForm/CreditCardFields/CreditCardFields.js